(ns web3.utils
  (:require [camel-snake-kebab.core :as case]
            [cljs.core.async :refer (chan put! close! <! >! timeout)]
            [clojure.string :as str]
            [clojure.walk :as walk]
            [goog.net.XhrIo :as xhr]
            [goog.dom :as gdom]
            [shodan.console :as log :include-macros true]
            [goog.dom :as dom]
            [reagent.core :as r]
            [applied-science.js-interop :as j])
  (:require-macros [cljs.core.async.macros :refer [go go-loop]]
                   [web3.utils :refer [for-map]]))

(defn get-element [selector]
  (.querySelector js/document selector))

(defn get*
  "get BUT its smart about when to use get vs get-in"
  [m k-or-p]
  (cond
    (keyword? k-or-p) (m k-or-p)
    (seqable? k-or-p) (get-in m k-or-p)
    :else nil))

(defn select-values [m ks]
  (reduce #(if-let [v (get* m %2)]
            (conj %1 v)
            (conj %1 :not-found)) [] ks))

(defn now
  []
  (.now js/Date))

(defn iso-now
  []
  (.toISOString (js/Date.)))

(defn k->js
  [k]
  (if (keyword? k)
    (cond->> (case/->camelCase (name k))
             (namespace k) (str (case/->camelCase (namespace k)) "/"))
    (str k)))

(defn js->k
  [s]
  (keyword (case/->kebab-case s)))

(defn walk-vals
  [f m]
  (let [f (fn [[k v]]
            [k (f v)])]
    (walk/postwalk (fn [x]
                     (if (map? x)
                       (into {} (map f x)) x)) m)))

(defn walk-keys
  [f m]
  (let [f (fn [[k v]]
            [(f k) v])]
    (walk/postwalk (fn [x]
                     (if (map? x)
                       (into {} (map f x))
                       x))
                   m)))

(defn ->js-map
  "convert clj map to js :test/some-key -> \"test/someKey\""
  [m]
  (->> m
       (walk-keys k->js)
       clj->js))

(defn ->clj-map
  "convert js map to clj  \"test/someKey\" -> :test/some-key"
  ([m] (->clj-map nil m))
  ([key-ns m]
   (let [m (js->clj m)]
     (cond
       (map? m) (walk-keys (comp (partial keyword key-ns) case/->kebab-case) m)
       (coll? m) (map (partial ->clj-map key-ns) m)
       :else m))))

(defn ->clj-map-shallow
  "Shallow-convert js map to clj"
  [x]
  (when x
    (->> (for [k (.keys js/Object x)]
           [(case/->kebab-case k) (aget x k)])
         (into {}))))

(defn parse-query-string [s]
  (-> (.decodeURIComponent js/window s)
      (str/replace (re-pattern "^\\?|\\#") "")
      (str/split #"&")
      (->> (map #(str/split % #"="))
           (into {})
           (->clj-map))))

(defn parse-query
  [uri]
  (-> uri
      (str/split #"\?")
      (second)
      (parse-query-string)))

(defn wrap-async
  ([f] (wrap-async f (chan)))
  ([f out]
   (fn [& args]
     (apply f (conj (vec args)
                    (fn [& results]
                      (if-let [e (.. js/chrome -runtime -lastError)]
                        (put! out (js/Error. (.. e -message)))
                        (put! out (or results [])))
                      (close! out))))
     out)))

(defn find-first [pred coll]
  (first (filter pred coll)))

(defn some-str [s]
  (when (and s
             (not= "" s)
             (not (re-find #"^\s+$" s)))
    s))

(defn logfx
  [cofx args]
  (log/log :info args)
  cofx)

(defn fempty
  "Like fnil, but checks for empty collection."
  [f default]
  (fn [coll & args]
    (if (empty? coll)
      (apply f default args)
      (apply f coll args))))

(defn update-in-when
  "Like update-in but returns m unchanged if key-seq is not present."
  [m key-seq f & args]
  (let [found (get-in m key-seq ::none)]
    (if-not (identical? ::none found)
      (assoc-in m key-seq (apply f found args))
      m)))

(defn map-vals
  "Build map k -> (f v) for [k v] in map, preserving the initial type"
  [f m]
  (cond
    (sorted? m)
    (reduce-kv (fn [out-m k v] (assoc out-m k (f v))) (sorted-map) m)
    (map? m)
    (persistent! (reduce-kv (fn [out-m k v] (assoc! out-m k (f v))) (transient {}) m))
    :else
    (for-map [[k v] m] k (f v))))

(defn map-keys
  "Build map (f k) -> v for [k v] in map m"
  [f m]
  (if (map? m)
    (persistent! (reduce-kv (fn [out-m k v] (assoc! out-m (f k) v)) (transient {}) m))
    (for-map [[k v] m] (f k) v)))

(defn remove-empty-vals
  "Remove empty vals from map"
  [m]
  (reduce-kv (fn [m k v]
               (cond-> m
                       (or (nil? v)
                           (and (sequential? v)
                                (empty? v))) (dissoc k))) m m))

(defn korks->ks
  [korks]
  (cond
    (keyword? korks) [korks]
    (sequential? korks) (vec korks)
    :else (throw (ex-info "korks must be a key or seq of ks"
                          {:korks korks}))))

;; update-in which handles empty paths as you'd expect.
;; from https://groups.google.com/forum/?hl=en#!topic/clojure/3lsguMP-SYs
(defn update-in*
  [m ks f & args]
  (if-let [[k & mk] (seq ks)]
    (if mk
      (assoc m k (apply update-in* (get m k) mk f args))
      (assoc m k (apply f (get m k) args)))
    (apply f m args)))

(defn throw-err
  [e]
  (when (instance? js/Error e)
    (throw e))
  e)

(defn to-node [html]
  (let [doc (js/document.createElement "html")]
    (set! (.-innerHTML doc) html)
    doc))

(defn fetch [url {:keys [format parse on-complete]
                  :or {format :text}}]
  (xhr/send url
            (fn [^js e]
              (let [extract-result (case format :text (fn [^js target]
                                                        (.getResponseText target))
                                                :json (fn [^js target]
                                                        (.getResponseJson target)))
                    parse (or parse identity)]
                (on-complete (parse (extract-result (.-target e))))))))

(defn fetch-c
  "Like `fetch` but returns a core.async channel"
  [url opts]
  (assert (not (:on-complete opts))
          "core.async version of fetch should not be passed a callback")
  (let [c (chan)]
    (fetch url (assoc opts :on-complete
                           (fn [result]
                             (put! c result))))
    c))

(defn error-stack-summary [e]
  (when-let [stack (some-> e (.-stack))]
    (->> (re-seq #"\[as ([^\]]+)\]" stack)
         (map second)
         (filter #(not (str/starts-with? % "cljs$core$IFn$_invoke"))))))

(defn current-stack []
  (try
    (throw (js/Error.))
    (catch js/Error e
      (rest (error-stack-summary e)))))

(defn simplify-keyword [k]
  (if-not (or (simple-keyword? k)
              (nil? k))
    (keyword (name k))
    k))

(defn take-vec
  "Take `n` elements from vector `v`, with a check to make sure we don't go out of bounds."
  [n v]
  (subvec v 0 (-> n
                  (min (count v))
                  (max 0))))

(defn take-last-vec
  "Take the last `n` elements from vector `v`, with a check to make sure we don't go out of bounds."
  [n v]
  (let [length (count v)]
    (subvec v (max 0 (- length n)) length)))

(defn keyword-root [kw]
  (or (namespace kw) (name kw)))

(defn ext
  [name]
  (some-> name
          (str/split #"\.")
          last
          (str/lower-case)))

(defn scrollable?
  "Returns true if window currently displays a scroll bar.

   (Not critical that this works in *every* browser, minor enhancement)"
  []
  (> (.-clientHeight (js/document.getElementById "web3"))
     (.-clientHeight js/document.body)))

(defn trim-leading-char
  "Remove leading slashes"
  [ch s]
  (cond-> s
          (= ch (first s)) (subs 1)))

(defn ensure-prefix
  "Ensure that string `s` begins with `prefix`"
  [prefix s]
  (cond->> s
           (not (str/starts-with? s prefix)) (str prefix)))


(defn link-data-decode
  [link]
  (when link
    (->> link
         (re-find #"data:,(.*)")
         second
         (.decodeURIComponent js/window))))

(defn link-data-encode
  [text]
  (when text
    (str "data:," (.encodeURIComponent js/window text))))

(def space \u00a0)

(defn target-value
  [e]
  (.. e -target -value))

(defn wrap-parens
  [s]
  (str "(" s ")"))

(defn toggle-set
  [s v]
  (let [f (if (get s v)
            disj
            (fnil conj #{}))]
    (f s v)))

(defn eval-if-fn [x]
  (if (fn? x)
    (x)
    x))

#_(defn submit-form [^js e]
    (.preventDefault e)
    (.stopPropagation e)
    (some-> (dom/getAncestorByTagNameAndClass (.-target e) "FORM")
            (.submit)))

(defn closest
  "Return element or first ancestor of element that matches predicate, like jQuery's .closest()."
  [el pred]
  (if (pred el)
    el
    (dom/getAncestor el pred)))

(defn format-date
  [date]
  (when date
    (let [date-obj (js/Date. date)]
      (str (inc (.getMonth date-obj)) "/"
           (.getDate date-obj) "/"
           (.getFullYear date-obj)))))

(defn flatten-keys
  "Flatten a map in which some keys are vectors of keys.
  {[:a, :b] 1} => {:a 1, :b 1}"
  [m]
  (->> m
       (reduce-kv (fn [handlers k v]
                    (->> (if (vector? k) k [k])
                         (reduce (fn [m k]
                                   (assoc m k v)) handlers)))
                  {})))

(defn focus-first-input* [^js root-element]
  (some-> root-element
          (gdom/findNode (fn [^js el]
                           (let [tagName (.-tagName el)]
                             (or (= "TEXTAREA" tagName)
                                 (and (= "INPUT" tagName)
                                      (= "text" (.getAttribute el "type")))
                                 (= "SELECT" tagName)))))

          (.focus)))

(defn focus-first-input! [component]
  (js/setTimeout
   #(some-> (r/dom-node component)
            (focus-first-input*))
   350))

;; from medley, https://github.com/weavejester/medley/blob/master/src/medley/core.cljc
(defn distinct-by
  "Returns a lazy sequence of the elements of coll, removing any elements that
  return duplicate values when passed to a function f."
  ([f]
   (fn [rf]
     (let [seen (volatile! #{})]
       (fn
         ([] (rf))
         ([result] (rf result))
         ([result x]
          (let [fx (f x)]
            (if (contains? @seen fx)
              result
              (do (vswap! seen conj fx)
                  (rf result x)))))))))
  ([f coll]
   (let [step (fn step [xs seen]
                (lazy-seq
                 ((fn [[x :as xs] seen]
                    (when-let [s (seq xs)]
                      (let [fx (f x)]
                        (if (contains? seen fx)
                          (recur (rest s) seen)
                          (cons x (step (rest s) (conj seen fx)))))))
                  xs seen)))]
     (step coll #{}))))

(defn coll-count [size coll]
  (let [n (-> coll
              (seq)
              (count))]
    (when (some-> n (pos?))
      [:div.round-100p.bg-darken-3.text-size-10.inline-flex.justify-center.items-center.margin-h-2.weight-bold.text-black
       {:style {:width size
                :height size}} n])))

(defn save-event?
  "Returns true if user pressed 'Enter' or alt/ctrl-s."
  ([e] (save-event? {} e))
  ([this e]
   (or (and (= "s" (.toLowerCase (.-key e)))
            (or (.-ctrlKey e) (.-metaKey e)))
       (and (= 13 (.-which e))
            (not (.-shiftKey e))
            (not (.-altKey e))
            (not (:multi-line? (:props this)))))))

(defn stop-prevent! [^js e]
  (.preventDefault e)
  (.stopPropagation e))

(defn strip-www
  [url]
  (str/replace url #"((http|https)://)*.*(www\.)" ""))

(defn is-external-href?
  [href]
  (when href (str/starts-with? href "http")))

(def domain (str (j/get-in js/window [:location :hostname])
                 (let [port (j/get-in js/window [:location :port])]
                   (when-not (or (nil? port)
                                 (empty? port))
                     (str ":" port)))))

(comment
 (= "yofoo.com"
    (strip-www "www.yofoo.com")
    (strip-www "http://www.yofoo.com")
    (strip-www "https://www.yofoo.com")))

(defonce on-reload* (atom {}))

(defn on-reload
  "Register a callback to fire when page reloads (during development)"
  [key f]
  (swap! on-reload* assoc key f))
